import { ConsiderationStatusBadge } from '@/components/badges/ConsiderationStatusBadge';
import { SubscriptionStatusBadge } from '@/components/badges/SubscriptionStatusBadge';
import { UserChip } from '@/components/UserChip';
import { useRowClick } from '@/lib/navigation';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
    activeHomesState,
    considerationsForConsiderationTypeState,
    homeTaskSubscriptionsForConsiderationTypeState,
} from '@/state';
import { useLoadableAtomValue } from '@/state/loadableHooks';
import type { Consideration, ConsiderationType, ConsiderationTypeId, Home } from '@rock/types';
import { orderBy } from 'lodash';
import type { FC } from 'react';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';

export const ConsiderationTypeHomesTab: FC<{ considerationType: ConsiderationType }> = ({ considerationType }) => {
    const typeId = considerationType.id;
    const [homes, homesLoadable] = useLoadableAtomValue(activeHomesState, []);
    const [subscriptions, subscriptionsLoadable] = useLoadableAtomValue(
        useMemo(() => homeTaskSubscriptionsForConsiderationTypeState(typeId), [typeId]),
        [],
    );
    const [considerations, considerationsLoadable] = useLoadableAtomValue(
        useMemo(() => considerationsForConsiderationTypeState(typeId), [typeId]),
        [],
    );

    const isLoading =
        homesLoadable.state === 'loading' ||
        subscriptionsLoadable.state === 'loading' ||
        considerationsLoadable.state === 'loading';
    const hasError =
        homesLoadable.state === 'hasError' ||
        subscriptionsLoadable.state === 'hasError' ||
        considerationsLoadable.state === 'hasError';

    const sortedHomes = useMemo(() => orderBy(homes, (h) => h.name.toLowerCase()), [homes]);

    const subscriptionsByHomeId = useMemo(() => {
        const map = new Map<string, boolean>();
        if (subscriptions) {
            for (const sub of subscriptions) {
                map.set(sub.homeId, true);
            }
        }
        return map;
    }, [subscriptions]);

    const considerationsByHomeId = useMemo(() => {
        const map = new Map<string, Consideration>();
        if (considerations) {
            for (const consideration of considerations) {
                // Only keep the most recent consideration per home (since we ordered by updatedAt desc),
                // except that a withdrawn row never wins: withdrawing stamps `updatedAt`, so it sorts to
                // the top and would deterministically mask a newer pending one.
                const held = map.get(consideration.homeId);
                if (!held) {
                    map.set(consideration.homeId, consideration);
                } else if (held.status === 'withdrawn' && consideration.status !== 'withdrawn') {
                    map.set(consideration.homeId, consideration);
                }
            }
        }
        return map;
    }, [considerations]);

    return (
        <div className="space-y-6">
            <h2 className="text-2xl font-semibold">Homes</h2>

            <Card>
                <CardHeader>
                    <CardTitle>Home Subscriptions</CardTitle>
                    <CardDescription>View which homes are subscribed to this consideration type.</CardDescription>
                </CardHeader>
                <CardContent className="overflow-x-auto">
                    {isLoading ? (
                        <div className="text-muted-foreground">Loading home subscriptions…</div>
                    ) : hasError ? (
                        <div className="text-destructive">
                            Error loading home subscriptions:{' '}
                            {homesLoadable.state === 'hasError'
                                ? homesLoadable.error instanceof Error
                                    ? homesLoadable.error.message
                                    : String(homesLoadable.error)
                                : subscriptionsLoadable.state === 'hasError'
                                  ? subscriptionsLoadable.error instanceof Error
                                      ? subscriptionsLoadable.error.message
                                      : String(subscriptionsLoadable.error)
                                  : considerationsLoadable.state === 'hasError'
                                    ? considerationsLoadable.error instanceof Error
                                        ? considerationsLoadable.error.message
                                        : String(considerationsLoadable.error)
                                    : 'Unknown error'}
                        </div>
                    ) : sortedHomes.length === 0 ? (
                        <div className="text-sm text-muted-foreground">No active homes found.</div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="min-w-[200px]">Home Name</TableHead>
                                    <TableHead className="min-w-[200px]">Owner</TableHead>
                                    <TableHead className="min-w-[120px]">Consideration</TableHead>
                                    <TableHead className="min-w-[120px]">Subscribed</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {sortedHomes.map((home) => {
                                    return (
                                        <HomeSubscriptionRow
                                            key={home.id}
                                            home={home}
                                            subscriptionsByHomeId={subscriptionsByHomeId}
                                            considerationsByHomeId={considerationsByHomeId}
                                        />
                                    );
                                })}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

const HomeSubscriptionRow: FC<{
    home: Home;
    subscriptionsByHomeId: Map<string, boolean>;
    considerationsByHomeId: Map<string, Consideration>;
}> = ({ home, subscriptionsByHomeId, considerationsByHomeId }) => {
    const isSubscribed = subscriptionsByHomeId.has(home.id);
    const consideration = considerationsByHomeId.get(home.id);
    const onRowClick = useRowClick();
    const typeId = useParams<{ typeId?: ConsiderationTypeId }>().typeId;

    return (
        <TableRow
            className="cursor-pointer transition-colors hover:bg-muted/50"
            onClick={onRowClick(`/directory/homes/${home.id}/considerations/${typeId}`)}
        >
            <TableCell>
                <div className="font-medium">{home.name}</div>
                <div className="text-xs text-muted-foreground">{home.id}</div>
            </TableCell>
            <TableCell>
                <UserChip userId={home.owner} size="sm" showEmail />
            </TableCell>
            <TableCell>
                <ConsiderationStatusBadge status={consideration?.status} />
            </TableCell>
            <TableCell>
                <SubscriptionStatusBadge isSubscribed={isSubscribed} variant="text" />
            </TableCell>
        </TableRow>
    );
};
